import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, IconButton } from "@mui/material";
import { useCallback, useState } from "react";

/** Duration for arrow-driven slide transitions (ms). */
const ARROW_SLIDE_DURATION_MS = 500;

type BannerCarouselProps = {
  banners: React.ReactNode[];
};

export default function BannerCarousel({ banners }: BannerCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [arrowSlideActive, setArrowSlideActive] = useState(false);
  const slideCount = banners.length;

  const goTo = useCallback(
    (index: number, animate = false) => {
      const next = ((index % slideCount) + slideCount) % slideCount;
      setArrowSlideActive(animate);
      setActiveIndex(next);
    },
    [slideCount]
  );

  const goPrev = useCallback(() => {
    goTo(activeIndex - 1, true);
  }, [activeIndex, goTo]);

  const goNext = useCallback(() => {
    goTo(activeIndex + 1, true);
  }, [activeIndex, goTo]);

  const handleSlideTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (e.target !== e.currentTarget || e.propertyName !== "transform") return;
    setArrowSlideActive(false);
  }, []);

  if (banners.length === 0) return <Box></Box>;

  if (banners.length === 1) return <Box>{banners[0]}</Box>;

  return (
    <Box>
      <Box
        role="region"
        aria-roledescription="carousel"
        aria-label="Home announcements"
        sx={{ overflow: "hidden", minHeight: 144.5 }}
      >
        <Box
          onTransitionEnd={handleSlideTransitionEnd}
          sx={{
            display: "flex",
            width: `${slideCount * 100}%`,
            transform: `translateX(-${(100 / slideCount) * activeIndex}%)`,
            transition: arrowSlideActive
              ? (theme) =>
                  theme.transitions.create("transform", {
                    duration: ARROW_SLIDE_DURATION_MS,
                  })
              : "none",
          }}
        >
          {banners.map((banner, index) => (
            <Box
              key={index}
              sx={{
                width: `${100 / slideCount}%`,
                flexShrink: 0,
                boxSizing: "border-box",
              }}
              aria-hidden={activeIndex !== index}
            >
              {banner}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1.5,
        }}
      >
        <Box component="ul" sx={{ display: "flex", alignItems: "center", gap: 0, listStyle: "none", m: 0, p: 0 }}>
          {banners.map((_banner, index) => (
            <Box component="li" key={index}>
              <IconButton
                type="button"
                size="small"
                onClick={() => goTo(index)}
                aria-label={`Show banner ${index + 1} of ${slideCount}`}
                aria-current={activeIndex === index ? "true" : undefined}
                sx={{
                  "&:hover .HomeBannerCarousel-dot": {
                    bgcolor: activeIndex === index ? "primary.dark" : "action.disabled",
                  },
                }}
              >
                <Box
                  className="HomeBannerCarousel-dot"
                  sx={{
                    width: 8,
                    height: 8,
                    padding: 0,
                    borderRadius: "50%",
                    bgcolor: activeIndex === index ? "primary.main" : "action.disabledBackground",
                    transition: (muiTheme) =>
                      muiTheme.transitions.create("background-color", {
                        duration: muiTheme.transitions.duration.shortest,
                      }),
                  }}
                />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          <IconButton type="button" size="small" onClick={goPrev} aria-label="Previous banner">
            <ChevronLeft fontSize="small" />
          </IconButton>
          <IconButton type="button" size="small" onClick={goNext} aria-label="Next banner">
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
